import { useState, useEffect, useCallback } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

interface Props {
  subscribeEndpoint: "/api/admin/push/subscribe" | "/api/client/push/subscribe";
}

export default function NotificationPermissionBanner({ subscribeEndpoint }: Props) {
  const [visible, setVisible] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const { data: vapidData } = useQuery<{ publicKey: string | null }>({
    queryKey: ["/api/push/vapid-key"],
  });

  useEffect(() => {
    if (!("Notification" in window) || !("PushManager" in window) || !("serviceWorker" in navigator)) return;
    if (Notification.permission === "default" && !dismissed) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [dismissed]);

  const handleAllow = useCallback(async () => {
    setSubscribing(true);
    try {
      const result = await Notification.requestPermission();
      if (result === "granted" && vapidData?.publicKey) {
        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey),
          });
        }
        const json = subscription.toJSON();
        await apiRequest("POST", subscribeEndpoint, {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
        });
        console.log("[Push] Subscribed via banner");
      }
      setVisible(false);
    } catch (err) {
      console.error("[Push] Banner subscribe error:", err);
    } finally {
      setSubscribing(false);
    }
  }, [vapidData?.publicKey, subscribeEndpoint]);

  if (!visible) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-3 mb-4 rounded-lg border bg-primary/5 border-primary/20" data-testid="notification-permission-banner">
      <Bell className="h-5 w-5 text-primary shrink-0" />
      <p className="text-sm flex-1">
        Vill du få aviseringar? Tryck på tillåt för att inte missa viktiga uppdateringar.
      </p>
      <Button
        size="sm"
        onClick={handleAllow}
        disabled={subscribing}
        data-testid="button-allow-notifications"
      >
        {subscribing ? "..." : "Tillåt"}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => { setDismissed(true); setVisible(false); }}
        data-testid="button-dismiss-notifications"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
